/**
 * Widget H: Rollover Opportunity
 * 
 * Displays a vertical bar chart showing the top 10 clients with
 * the highest rollover opportunity values (existing 401(k) + annuity-eligible amounts).
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateRolloverOpportunities, RolloverOpportunityData } from "../utils/widgetCalculations";
import { getRolloverValue } from "../utils/calculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR, getUnifiedChartColor } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface RolloverOpportunityWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

export function RolloverOpportunityWidget({
  clients,
  isLoading = false,
  userId,
}: RolloverOpportunityWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["rollover-opportunity", userId],
    queryFn: async () => await api.getRolloverOpportunity(userId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const data = useMemo(() => {
    let result: {
      opportunities: Array<{
        clientId: number | string;
        clientName: string;
        rolloverValue: number;
      }>;
    };
    
    console.log("🔍 RolloverOpportunityWidget: Processing data - apiData:", apiData);
    console.log("🔍 RolloverOpportunityWidget: apiData is null?", apiData === null);
    console.log("🔍 RolloverOpportunityWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 RolloverOpportunityWidget: Clients available:", clients.length);
    
    if (apiData && apiData.opportunities && apiData.opportunities.length > 0) {
      // Verify API data quality - check for suspicious patterns (all same names/values)
      const uniqueNames = new Set(apiData.opportunities.map((o: any) => o.clientName));
      const uniqueValues = new Set(apiData.opportunities.map((o: any) => o.rolloverValue));
      const uniqueClientIds = new Set(apiData.opportunities.map((o: any) => o.clientId));
      
      console.log("🔍 RolloverOpportunityWidget: API opportunities:", JSON.stringify(apiData.opportunities.slice(0, 5), null, 2));
      console.log("🔍 RolloverOpportunityWidget: Unique client names in API:", uniqueNames.size, "out of", apiData.opportunities.length);
      console.log("🔍 RolloverOpportunityWidget: Unique rollover values in API:", uniqueValues.size, "out of", apiData.opportunities.length);
      console.log("🔍 RolloverOpportunityWidget: Unique client IDs in API:", uniqueClientIds.size, "out of", apiData.opportunities.length);
      
      // If API data looks suspicious (all same names or all same values), use calculated data instead
      const isSuspiciousData = uniqueNames.size <= 2 || uniqueValues.size <= 2 || uniqueClientIds.size < apiData.opportunities.length;
      
      if (isSuspiciousData && clients.length > 0) {
        console.log("⚠️ RolloverOpportunityWidget: API data looks suspicious (duplicate names/values), calculating from clients instead");
        try {
          const calculatedData = calculateRolloverOpportunities(clients);
          if (calculatedData.opportunities.length > 0) {
            console.log("✅ RolloverOpportunityWidget: Using calculated data from clients (API data was suspicious)");
            result = calculatedData;
          } else {
            console.log("⚠️ RolloverOpportunityWidget: Calculated data also empty, using API data despite suspicion");
            result = apiData;
          }
        } catch (error) {
          console.error("❌ RolloverOpportunityWidget: Error calculating from clients:", error);
          result = apiData; // Fall back to API data even if suspicious
        }
      } else {
        // API data looks good - use it
        console.log("✅ RolloverOpportunityWidget: Using API data (looks valid)");
        result = apiData;
      }
    } else if (clients.length > 0) {
      // API data is empty or unavailable - try calculating from clients
      console.log("⚠️ RolloverOpportunityWidget: API data empty or unavailable, calculating from client data");
      try {
        // Debug: Check how many clients are 401k/403b type
        const rolloverEligibleClients = clients.filter(c => c.type === "401k" || c.type === "403b");
        console.log("🔍 RolloverOpportunityWidget: Rollover-eligible clients (401k/403b):", rolloverEligibleClients.length, "out of", clients.length);
        
        // Debug: Check sample client data
        if (rolloverEligibleClients.length > 0) {
          const sampleClient = rolloverEligibleClients[0];
          const calculatedRollover = getRolloverValue(sampleClient);
          console.log("🔍 RolloverOpportunityWidget: Sample client:", {
            id: sampleClient.id,
            type: sampleClient.type,
            firstName: sampleClient.firstName,
            lastName: sampleClient.lastName,
            name: `${sampleClient.firstName || ""} ${sampleClient.lastName || ""}`.trim(),
            t_401k_existing401kValue: sampleClient.t_401k_existing401kValue,
            t_403b_currentBalance: sampleClient.t_403b_currentBalance,
            annuityValue: (sampleClient as any).annuityValue,
            calculatedRolloverValue: calculatedRollover,
          });
          
          // Check multiple clients to see if they all have the same values
          if (rolloverEligibleClients.length > 1) {
            const secondClient = rolloverEligibleClients[1];
            const secondRollover = getRolloverValue(secondClient);
            console.log("🔍 RolloverOpportunityWidget: Second client:", {
              id: secondClient.id,
              type: secondClient.type,
              firstName: secondClient.firstName,
              lastName: secondClient.lastName,
              name: `${secondClient.firstName || ""} ${secondClient.lastName || ""}`.trim(),
              t_401k_existing401kValue: secondClient.t_401k_existing401kValue,
              t_403b_currentBalance: secondClient.t_403b_currentBalance,
              annuityValue: (secondClient as any).annuityValue,
              calculatedRolloverValue: secondRollover,
            });
          }
        }
        
        const calculatedData = calculateRolloverOpportunities(clients);
        console.log("🔍 RolloverOpportunityWidget: Calculated from clients - Opportunities:", calculatedData.opportunities.length);
        console.log("🔍 RolloverOpportunityWidget: Calculated opportunities:", JSON.stringify(calculatedData.opportunities.slice(0, 5), null, 2));
        
        if (calculatedData.opportunities.length > 0) {
          console.log("✅ RolloverOpportunityWidget: Using calculated data from clients");
          result = calculatedData;
        } else {
          console.log("⚠️ RolloverOpportunityWidget: Calculated data also empty - clients may not have rollover values");
          result = {
            opportunities: [],
          };
        }
      } catch (error) {
        console.error("❌ RolloverOpportunityWidget: Error calculating from clients:", error);
        result = {
          opportunities: [],
        };
      }
    } else {
      // No API data and no clients - return empty structure
      console.log("❌ RolloverOpportunityWidget: No API data and no clients, returning empty structure");
      result = {
        opportunities: [],
      };
    }
    
    // Ensure we have opportunities data
    if (!result || !result.opportunities || result.opportunities.length === 0) {
      return { chartData: [], totalOpportunities: 0 };
    }
    
    // Transform to chart data format - take top 10 and sort by value descending
    const sortedOpportunities = result.opportunities
      .slice(0, 10)
      .sort((a, b) => b.rolloverValue - a.rolloverValue);
    
    const chartData = sortedOpportunities.map((opp, index) => {
      const color = getUnifiedChartColor(index);
      return {
        name: opp.clientName.length > 20 ? `${opp.clientName.substring(0, 17)}...` : opp.clientName,
        fullName: opp.clientName,
        value: Number(opp.rolloverValue) || 0, // Ensure it's a number
        fill: color,
        clientId: opp.clientId,
      };
    });

    return { chartData, totalOpportunities: result.opportunities.length };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No rollover opportunities found for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.chartData}
              margin={BAR_CHART_MARGINS}
            >
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Rollover Value", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                tick={AXIS_STYLE.tick}
                tickFormatter={formatCurrency}
                tickLine={false}
                width={80}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value: number) => [formatCurrency(value), ""]}
                  labelFormatter={(label: string) => {
                    const dataPoint = data.chartData.find(d => d.name === label);
                    return dataPoint?.fullName || label;
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="value" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => {
                  const color = entry.fill || getUnifiedChartColor(index);
                  return (
                    <Cell 
                      key={`cell-${entry.clientId}-${index}`} 
                      fill={color}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 pt-3 border-t">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Showing top {data.chartData.length} of {data.totalOpportunities} opportunities
            </p>
            {data.chartData.length > 0 && (
              <p className="text-xs font-medium text-foreground">
                Total Value: {formatCurrency(data.chartData.reduce((sum, item) => sum + item.value, 0))}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

